import {
  Box,
  Divider,
  Flex,
  Icon,
  Link,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tab,
  Tooltip,
  Text,
} from 'pounceV2';
import React from 'react';
import { useFormikContext, FastField, Field } from 'formik';
import { SIGNALS_DOC_URL } from 'Source/constants';
import DestinationOverridesFieldV3 from 'Components/fields/DestinationOverrides/DestinationOverridesV3';
import { FormikTextAreaV3 } from 'Components/fields/TextArea';
import BaseDetectionFormMappingsSectionV3 from 'Components/forms/BaseDetectionForm/BaseDetectionFormMappingsSection/BaseDetectionFormMappingsSectionV3';
import FormikMultiComboboxV3 from 'Components/fields/MultiComboBox/MultiComboBoxV3';
import { FormikSwitchV3 } from 'Components/fields/Switch';
import SeverityFieldV3 from 'Pages/Build/Detections/Rules/SimpleRuleForm/SeverityFieldV3';
import { SimpleRuleFormValues } from 'Pages/Build/Detections/Rules/SimpleRuleForm/SimpleRuleFormV3';
import FieldTitle from 'Components/forms/BaseDetectionForm/AlertSettingsFieldTitle/AlertSettingsFieldTitleV3';

const AlertSettingsFormV3 = ({ readOnly = false }: { readOnly: boolean }) => {
  const { values } = useFormikContext<SimpleRuleFormValues>();
  const tagAdditionValidation = React.useCallback(
    (tag: string) => !values.tags.includes(tag),
    [values.tags],
  );

  return (
    <Box px={2} height="100%" overflow="hidden">
      <Tabs as={Box} height="100%" variant="underline" display="flex" flexDirection="column">
        <TabList>
          <Tab>BASIC</Tab>
          {values.createAlert ? <Tab>CONTEXT</Tab> : null}
        </TabList>
        <Divider m={0} />
        <Box
          style={{
            height: '100%',
            overflowY: 'scroll',
          }}
        >
          <TabPanels>
            <TabPanel>
              <Flex direction="column" gap={6} pt={8}>
                <Flex direction="row" gap={3} align="center">
                  <FieldTitle>Create Alert</FieldTitle>
                  <Field
                    disabled={readOnly}
                    data-testid="create-alert-toggle"
                    name="createAlert"
                    aria-label="Create Alert"
                    as={FormikSwitchV3}
                  />
                  <Tooltip
                    content={
                      <Box maxWidth={300}>
                        <Text wordBreak="break-word">
                          This toggle controls whether a rule will trigger an alert on matches in
                          addition to creating signals on matches and is set to On by default. You
                          may want to disable this toggle when creating signals for use with
                          correlation rules. Click to learn more.
                        </Text>
                      </Box>
                    }
                  >
                    <Link href={SIGNALS_DOC_URL} external>
                      <Icon type="info" size="large" cursor="pointer" />
                    </Link>
                  </Tooltip>
                </Flex>
                {values.createAlert ? (
                  <>
                    <Box>
                      <FieldTitle>Alert Severity</FieldTitle>
                      <SeverityFieldV3 canEdit={!readOnly} />
                    </Box>
                    <Box>
                      <FieldTitle>Destination</FieldTitle>
                      <DestinationOverridesFieldV3 />
                    </Box>
                  </>
                ) : null}
              </Flex>
            </TabPanel>
            {values.createAlert ? (
              <TabPanel>
                <Flex direction="column" gap={6} py={8} pr={1}>
                  <Box>
                    <FieldTitle>Description</FieldTitle>
                    <FastField
                      resize="vertical"
                      as={FormikTextAreaV3}
                      label="Description"
                      placeholder="Additional context about this Rule"
                      name="description"
                      disabled={readOnly}
                      maxRows={3}
                    />
                  </Box>
                  <Box>
                    <FieldTitle>Runbook</FieldTitle>
                    <FastField
                      as={FormikTextAreaV3}
                      label="Runbook"
                      placeholder="Procedures and operations related to this Rule"
                      name="runbook"
                      maxRows={3}
                      disabled={readOnly}
                    />
                  </Box>
                  <Box>
                    <FieldTitle>Reference</FieldTitle>
                    <FastField
                      resize="vertical"
                      as={FormikTextAreaV3}
                      label="Reference"
                      placeholder="An external link to why this Rule exists"
                      name="reference"
                      disabled={readOnly}
                    />
                  </Box>
                  <Box>
                    <FieldTitle>Summary Attributes</FieldTitle>
                    <Field
                      as={FormikMultiComboboxV3}
                      label="Summary Attributes"
                      name="summaryAttributes"
                      items={values.summaryAttributes}
                      searchable
                      allowAdditions
                      placeholder="What should we showcase in alerts?"
                      disabled={readOnly}
                      aria-describedby="summaryAttributes-description"
                    />
                  </Box>
                  <Box>
                    <FieldTitle>Tags</FieldTitle>
                    <FastField
                      as={FormikMultiComboboxV3}
                      searchable
                      containerProps={{ flex: 1 }}
                      name="tags"
                      label="Tags"
                      items={values.tags}
                      allowAdditions
                      validateAddition={tagAdditionValidation}
                      placeholder="i.e. HIPAA (separate with <Enter>)"
                      disabled={readOnly}
                    />
                  </Box>
                  <BaseDetectionFormMappingsSectionV3 canEdit={!readOnly} />
                </Flex>
              </TabPanel>
            ) : null}
          </TabPanels>
        </Box>
      </Tabs>
    </Box>
  );
};

export default AlertSettingsFormV3;
